
import { getSelectedBursts } from "@/helpers/store";
import { defineComponent, onMounted, reactive, toRefs } from "vue";
import VueFeather from "vue-feather";
import { groupBursts, fetchBurstDataArray, PlotableData } from "@/helpers/helpers";
import { plot, plotConditionMatchedPsd } from "@/helpers/plot";
import { fetchSimilarPeriods } from "@/helpers/api";
import { Temporal } from "@js-temporal/polyfill";
import LoadingSpinner from "@/components/LoadingSpinner.vue";

export default defineComponent({
  components: {
    VueFeather,
    LoadingSpinner,
  },
  setup() {
    const state = reactive({
      selectedPages: getSelectedBursts(),
      data: null as null | PlotableData[],
      isLoading: true,
      sensors: null as null | string[],
      axes: ["X", "Y", "Z"],
      crossesBoundary: false,
      psdScale: "log" as "log" | "loglinear",
      psdSelectedAxis: "X" as "X" | "Y" | "Z",
    });

    onMounted(async () => {
      await refreshData();
      plotGraphs();
    });

    // `Temporal.Instant.epochSeconds` truncates to whole seconds - it doesn't round, it just
    // drops any fractional part. Burst StartTimes carry sub-second precision (tied to actual
    // sample acquisition, not aligned to whole-second ticks), and condition_matching_on_data's
    // windows preserve that same fractional offset all the way through, so truncating it away
    // here would make the query's StartTime/EndTime boundaries undershoot the real values by
    // up to a second - silently excluding rows that genuinely fall in the period. The API
    // already accepts fractional epoch seconds (time_param does float(k)), so send
    // millisecond precision instead - via integer arithmetic on epochMilliseconds, not a
    // float division, to avoid any IEEE754 rounding risk on the seconds value.
    const toEpochSecondsString = (instant: Temporal.Instant): string => {
      const ms = instant.epochMilliseconds;
      const seconds = Math.trunc(ms / 1000);
      const millis = ms - seconds * 1000;
      return millis === 0 ? String(seconds) : `${seconds}.${String(millis).padStart(3, "0")}`;
    };

    // Fetches the condition-matched PSD windows (from S3, via the datastore-backed
    // condition_matching table) covering each selected sensor's own burst period, for the
    // currently toggled axis. One call per sensor/node - each returns every 200s sub-window
    // condition_matching_on_data found in that period, not just one aggregate PSD.
    const fetchConditionMatchedSeries = async () => {
      if (state.data === null) {
        return [];
      }
      return Promise.all(
        state.data.map(async ({ SensorName, SensorTags, periodStart, periodEnd }) => {
          const params = new URLSearchParams();
          params.set("axis", state.psdSelectedAxis);
          params.set("StartTime", toEpochSecondsString(Temporal.Instant.from(periodStart)));
          params.set("EndTime", toEpochSecondsString(Temporal.Instant.from(periodEnd)));
          params.set("SensorTags.Aggregator", SensorTags.Aggregator);
          params.set("SensorTags.NodeAddress", SensorTags.NodeAddress);
          const periods = await fetchSimilarPeriods(params);
          return { label: SensorName, periods };
        })
      );
    };

    const plotConditionMatchedGraph = async () => {
      if (state.data === null) {
        return;
      }
      const series = await fetchConditionMatchedSeries();
      await plotConditionMatchedPsd(series, state.psdScale, "PSD-graph");
    };

    const plotGraphs = async () => {
      if (state.data === null) {
        await refreshData();
      }
      if (state.data !== null) {
        plot(state.data);
        await plotConditionMatchedGraph();
      }
    };

    const refreshData = async () => {
      state.isLoading = true;
      const selectedBursts = await fetchBurstDataArray(getSelectedBursts().value);
      if (selectedBursts.length) {
        const [combinedData, crossesBoundary] = groupBursts(selectedBursts);
        state.data = combinedData;
        state.sensors = combinedData.map(({ SensorName }) => SensorName);
        state.crossesBoundary = crossesBoundary;
      }
      state.isLoading = false;
    };

    const replotPSD = async () => {
      state.isLoading = true;
      await plotConditionMatchedGraph();
      state.isLoading = false;
    };

    return {
      ...toRefs(state),
      getSelectedBursts,
      plotGraphs,
      refreshData,
      replotPSD,
    };
  },
});
